import { HttpErrorResponse, HttpRequest, HttpStatusCode } from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
	PaymentContractBackendPayload,
	PaymentContractStripePayload,
	PaymentUiError,
	PaymentUiErrorCategory
} from '@interfaces'
import { StripeError } from '@stripe/stripe-js'

const PAYMENT_ROUTE_HINTS = ['/subscriberplan', '/subscriberpaymethod']

const BACKEND_ERROR_CODE_TO_MESSAGE_KEY: Record<string, string> = {
	PLAN_NOT_FOUND: 'PLAN_NOT_FOUND',
	SUBSCRIBER_DUPLICATED_EMAIL: 'SUBSCRIBER_DUPLICATED_EMAIL',
	SUBSCRIBER_PLAN_ERROR: 'SUBSCRIBER_PLAN_ERROR',
	SUBSCRIBER_PAYMENT_ERROR: 'SUBSCRIBER_PAYMENT_ERROR',
	SUBSCRIBER_PAYMENT_STRIPE_ERROR: 'SUBSCRIBER_PAYMENT_STRIPE_ERROR',
	UNDEFINED_PLAN_ID: 'UNDEFINED_PLAN_ID',
	UNDEFINED_PAYMENT_TOKEN: 'UNDEFINED_PAYMENT_TOKEN'
}

const STRIPE_CODE_TO_MESSAGE_KEY: Record<string, string> = {
	AUTHENTICATION_REQUIRED: 'AUTHENTICATION_REQUIRED',
	CARD_NOT_SUPPORTED: 'CARD_NOT_SUPPORTED',
	CARD_VELOCITY_EXCEEDED: 'CARD_VELOCITY_EXCEEDED',
	DUPLICATE_TRANSACTION: 'DUPLICATE_TRANSACTION',
	EXPIRED_CARD: 'EXPIRED_CARD',
	INCORRECT_CVC: 'INCORRECT_CVC',
	INCOMPLETE_CVC: 'INCOMPLETE_CVC',
	INSUFFICIENT_FUNDS: 'INSUFFICIENT_FUNDS',
	INVALID_CVC: 'INVALID_CVC',
	LOST_CARD: 'LOST_CARD',
	PICKUP_CARD: 'PICKUP_CARD',
	PROCESSING_ERROR: 'PROCESSING_ERROR',
	RESTRICTED_CARD: 'RESTRICTED_CARD',
	REVOCATION_OF_ALL_AUTHORIZATIONS: 'REVOCATION_OF_ALL_AUTHORIZATIONS',
	REVOCATION_OF_AUTHORIZATION: 'REVOCATION_OF_AUTHORIZATION',
	STOLEN_CARD: 'STOLEN_CARD',
	STOP_PAYMENT_ORDER: 'STOP_PAYMENT_ORDER'
}

const CARD_DATA_ERROR_CODES = new Set([
	'EXPIRED_CARD',
	'INCORRECT_CVC',
	'INCOMPLETE_CVC',
	'INCOMPLETE_EXPIRY',
	'INCOMPLETE_NUMBER',
	'INCORRECT_NUMBER',
	'INVALID_CVC',
	'INVALID_EXPIRY_MONTH',
	'INVALID_EXPIRY_YEAR',
	'INVALID_NUMBER'
])

const TEMPORARY_ERROR_CODES = new Set([
	'API_CONNECTION_ERROR',
	'API_ERROR',
	'ISSUER_NOT_AVAILABLE',
	'NETWORK_ERROR',
	'PROCESSING_ERROR',
	'RATE_LIMIT_ERROR'
])

@Injectable({
	providedIn: 'root'
})
export class PaymentContractService {
	private readonly paymentRouteHints = PAYMENT_ROUTE_HINTS

	isPaymentRequest(request: HttpRequest<unknown> | string): boolean {
		const url = typeof request === 'string' ? request : request.url
		const normalizedUrl = url.toLowerCase()
		return this.paymentRouteHints.some(hint => normalizedUrl.includes(hint))
	}

	shouldBypassGlobalHandling(request: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
		if (this.isPaymentRequest(request)) {
			return true
		}

		return error.status === HttpStatusCode.PaymentRequired && !!this.extractBackendPayload(error)
	}

	resolveUnknownError(error: unknown): PaymentUiError {
		if (error instanceof HttpErrorResponse) {
			return this.resolveHttpError(error)
		}

		const stripePayload = this.extractStripePayload(error)
		if (stripePayload) {
			return this.resolveStripePayload(stripePayload)
		}

		return this.buildFallbackError('unexpected')
	}

	resolveHttpError(error: HttpErrorResponse): PaymentUiError {
		const payload = this.extractBackendPayload(error)
		if (!payload) {
			return this.buildFallbackError('unexpected', error.status)
		}

		const normalizedErrorCode = this.normalizeCode(payload.errorCode || payload.code)
		const stripeCode = this.normalizeCode(payload.stripe?.code || payload.stripe?.declineCode)
		const category = this.classifyCategory(payload, error.status, normalizedErrorCode, stripeCode)
		const recoverability = this.normalizeCode(payload.recoverability)
		const messageKey =
			BACKEND_ERROR_CODE_TO_MESSAGE_KEY[normalizedErrorCode] ||
			STRIPE_CODE_TO_MESSAGE_KEY[stripeCode] ||
			this.getDefaultMessageKey(category)

		return {
			titleKey: 'PAYMENT_ERROR_TITLE',
			messageKey,
			guidanceKey: this.resolveGuidanceKey(category, recoverability),
			category,
			recoverability: recoverability || 'UNKNOWN',
			statusCode: error.status,
			errorCode: normalizedErrorCode || stripeCode || undefined
		}
	}

	resolveStripeError(error: StripeError | null | undefined): PaymentUiError {
		return this.resolveStripePayload(this.extractStripePayload(error))
	}

	getStripeElementMessageKey(error: StripeError | null | undefined): string {
		if (!error) return ''

		return this.resolveStripeError(error).messageKey
	}

	private resolveStripePayload(payload: PaymentContractStripePayload | null): PaymentUiError {
		const normalizedStripeCode = this.normalizeCode(payload?.code || payload?.declineCode)
		const normalizedStatus = this.normalizeCode(payload?.status)
		const category: PaymentUiErrorCategory =
			normalizedStatus === 'REQUIRES_ACTION' || normalizedStripeCode === 'AUTHENTICATION_REQUIRED'
				? 'authentication_required'
				: CARD_DATA_ERROR_CODES.has(normalizedStripeCode)
					? 'card_data'
					: TEMPORARY_ERROR_CODES.has(normalizedStripeCode)
						? 'temporary'
						: normalizedStripeCode
							? 'bank_declined'
							: 'unexpected'

		return {
			titleKey: 'PAYMENT_ERROR_TITLE',
			messageKey: STRIPE_CODE_TO_MESSAGE_KEY[normalizedStripeCode] || this.getDefaultMessageKey(category),
			guidanceKey: this.resolveGuidanceKey(category, ''),
			category,
			recoverability: 'UNKNOWN',
			errorCode: normalizedStripeCode || undefined
		}
	}

	private classifyCategory(
		payload: PaymentContractBackendPayload,
		status: number,
		normalizedErrorCode: string,
		normalizedStripeCode: string
	): PaymentUiErrorCategory {
		const normalizedCategory = this.normalizeCode(payload.category)
		const normalizedRecoverability = this.normalizeCode(payload.recoverability)
		const normalizedStripeStatus = this.normalizeCode(payload.stripe?.status)

		if (
			status === HttpStatusCode.BadRequest ||
			status === HttpStatusCode.NotFound ||
			status === HttpStatusCode.Conflict ||
			normalizedCategory === 'DOMAIN' ||
			normalizedCategory === 'BUSINESS'
		) {
			return 'domain'
		}

		if (
			normalizedCategory === 'AUTHENTICATION_REQUIRED' ||
			normalizedRecoverability.includes('AUTH') ||
			normalizedRecoverability.includes('ACTION') ||
			normalizedStripeStatus === 'REQUIRES_ACTION' ||
			normalizedStripeCode === 'AUTHENTICATION_REQUIRED' ||
			normalizedErrorCode.includes('AUTH')
		) {
			return 'authentication_required'
		}

		if (CARD_DATA_ERROR_CODES.has(normalizedStripeCode) || CARD_DATA_ERROR_CODES.has(normalizedErrorCode)) {
			return 'card_data'
		}

		if (
			status === HttpStatusCode.PaymentRequired &&
			(TEMPORARY_ERROR_CODES.has(normalizedStripeCode) || TEMPORARY_ERROR_CODES.has(normalizedErrorCode))
		) {
			return 'temporary'
		}

		if (status === HttpStatusCode.PaymentRequired || normalizedCategory === 'PAYMENT' || !!payload.stripe) {
			return 'bank_declined'
		}

		return 'unexpected'
	}

	private resolveGuidanceKey(category: PaymentUiErrorCategory, normalizedRecoverability: string): string {
		if (normalizedRecoverability.includes('AUTH') || normalizedRecoverability.includes('ACTION')) {
			return 'PAYMENT_GUIDANCE_COMPLETE_AUTH'
		}

		if (normalizedRecoverability.includes('RETRY') || normalizedRecoverability.includes('TEMPORARY')) {
			return 'PAYMENT_GUIDANCE_RETRY_LATER'
		}

		if (
			normalizedRecoverability.includes('PAYMENT_METHOD') ||
			normalizedRecoverability.includes('CARD') ||
			normalizedRecoverability.includes('DECLINED')
		) {
			return 'PAYMENT_GUIDANCE_USE_ANOTHER_CARD'
		}

		if (normalizedRecoverability.includes('SUPPORT') || normalizedRecoverability.includes('CONTACT')) {
			return 'PAYMENT_GUIDANCE_CONTACT_SUPPORT'
		}

		switch (category) {
			case 'card_data':
				return 'PAYMENT_GUIDANCE_CHECK_CARD_DATA'
			case 'authentication_required':
				return 'PAYMENT_GUIDANCE_COMPLETE_AUTH'
			case 'bank_declined':
				return 'PAYMENT_GUIDANCE_USE_ANOTHER_CARD'
			case 'temporary':
				return 'PAYMENT_GUIDANCE_RETRY_LATER'
			case 'domain':
				return 'PAYMENT_GUIDANCE_REVIEW_REQUEST'
			default:
				return 'PAYMENT_GUIDANCE_CONTACT_SUPPORT'
		}
	}

	private getDefaultMessageKey(category: PaymentUiErrorCategory): string {
		switch (category) {
			case 'card_data':
				return 'PAYMENT_ERROR_CARD_DATA'
			case 'authentication_required':
				return 'AUTHENTICATION_REQUIRED'
			case 'bank_declined':
				return 'PAYMENT_ERROR_BANK_DECLINED'
			case 'temporary':
				return 'PAYMENT_ERROR_TEMPORARY'
			case 'domain':
				return 'PAYMENT_ERROR_DOMAIN'
			default:
				return 'PAYMENT_ERROR_UNEXPECTED'
		}
	}

	private buildFallbackError(category: PaymentUiErrorCategory, statusCode?: number): PaymentUiError {
		return {
			titleKey: 'PAYMENT_ERROR_TITLE',
			messageKey: this.getDefaultMessageKey(category),
			guidanceKey: this.resolveGuidanceKey(category, ''),
			category,
			recoverability: 'UNKNOWN',
			statusCode
		}
	}

	private extractBackendPayload(error: unknown): PaymentContractBackendPayload | null {
		const source = error instanceof HttpErrorResponse ? error.error : error
		const candidates = [
			source,
			this.tryGet(source, 'error'),
			this.tryGet(source, 'details'),
			this.tryGet(source, 'payload')
		]

		for (const candidate of candidates) {
			if (this.looksLikeBackendPayload(candidate)) {
				return candidate
			}
		}

		return null
	}

	private extractStripePayload(error: unknown): PaymentContractStripePayload | null {
		if (!error || typeof error !== 'object') return null

		const stripeError = error as StripeError & {
			decline_code?: string
			payment_intent?: { id?: string; status?: string; client_secret?: string }
		}

		const directCode = this.tryGet(error, 'code')
		const directDeclineCode = this.tryGet(error, 'declineCode') || this.tryGet(error, 'decline_code')
		const paymentIntent = stripeError.payment_intent

		if (!directCode && !directDeclineCode && !paymentIntent?.status && !paymentIntent?.id) {
			return null
		}

		return {
			type: this.asString(this.tryGet(error, 'type')),
			code: this.asString(directCode),
			declineCode: this.asString(directDeclineCode),
			paymentIntentId: this.asString(this.tryGet(error, 'paymentIntentId')) || paymentIntent?.id,
			status: this.asString(this.tryGet(error, 'status')) || paymentIntent?.status,
			clientSecret: this.asString(this.tryGet(error, 'clientSecret')) || paymentIntent?.client_secret
		}
	}

	private looksLikeBackendPayload(value: unknown): value is PaymentContractBackendPayload {
		if (!value || typeof value !== 'object') return false

		const record = value as Record<string, unknown>
		return (
			typeof record['errorCode'] === 'string' ||
			typeof record['recoverability'] === 'string' ||
			typeof record['category'] === 'string' ||
			(typeof record['stripe'] === 'object' && !!record['stripe'])
		)
	}

	private normalizeCode(value: unknown): string {
		if (typeof value !== 'string') return ''

		return value
			.trim()
			.replace(/([a-z0-9])([A-Z])/g, '$1_$2')
			.replace(/[\s.-]+/g, '_')
			.toUpperCase()
	}

	private tryGet(source: unknown, key: string): unknown {
		if (!source || typeof source !== 'object') return undefined
		return (source as Record<string, unknown>)[key]
	}

	private asString(value: unknown): string | undefined {
		return typeof value === 'string' ? value : undefined
	}
}
